(() => {
  if (window.vorugTaleMotion || !Element.prototype.animate || !window.IntersectionObserver) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const ease = 'cubic-bezier(.16,1,.3,1)';
  const active = new Set();
  const jobs = new WeakMap();
  const animate = (element, frames, duration = 700, delay = 0) => {
    if (!element || reduced.matches) return;
    const animation = element.animate(frames, {duration, delay, easing: ease, fill: 'backwards'});
    active.add(animation);
    const release = () => { active.delete(animation); animation.cancel(); };
    animation.finished.then(release, () => active.delete(animation));
    return animation;
  };
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      const job = jobs.get(entry.target);
      jobs.delete(entry.target);
      if (job && !reduced.matches) job();
    });
  }, {rootMargin: '0px 0px -8% 0px', threshold: 0.08});
  const enter = (element, job) => {
    if (!element || reduced.matches) return;
    // Deep-link arrivals keep already-passed chapters settled and readable.
    if (element.getBoundingClientRect().bottom < 0) return;
    jobs.set(element, job); observer.observe(element);
  };
  const words = heading => {
    if (!heading || heading.dataset.taleWords) return heading ? [...heading.querySelectorAll('.vte-word-inner')] : [];
    heading.dataset.taleWords = 'true';
    const text = heading.innerText;
    heading.setAttribute('aria-label', text.trim());
    const fragment = document.createDocumentFragment();
    text.split(/(\s+)/).forEach(part => {
      if (/^\s+$/.test(part)) { fragment.append(part.includes('\n') ? document.createElement('br') : document.createTextNode(part)); return; }
      if (!part) return;
      const outer = document.createElement('span');
      outer.className = 'vte-word'; outer.setAttribute('aria-hidden', 'true');
      const inner = document.createElement('span'); inner.className = 'vte-word-inner'; inner.textContent = part;
      outer.append(inner); fragment.append(outer);
    });
    heading.replaceChildren(fragment);
    return [...heading.querySelectorAll('.vte-word-inner')];
  };
  const whenImageReady = (photo, job) => {
    const image = photo?.querySelector('img');
    if (!image) return;
    const reveal = () => {
      const bounds = photo.getBoundingClientRect();
      if (bounds.bottom > 0 && bounds.top < innerHeight && !reduced.matches) job();
    };
    if (image.complete && image.naturalWidth) reveal();
    else image.addEventListener('load', reveal, {once: true});
  };
  const imageArrival = (photo, direction, duration = 800) => {
    if (!photo) return;
    const crop = direction === 'side' ? 'inset(0 100% 0 0)' : 'inset(0 0 100% 0)';
    animate(photo, [{clipPath: crop}, {clipPath: 'inset(0 0 0 0)'}], duration);
    animate(photo.querySelector('img'), [{transform: 'scale(1.035)'}, {transform: 'scale(1)'}], duration + 250);
  };
  const setup = (root = document) => {
    root.querySelectorAll('.vte').forEach(section => {
      if (section.dataset.taleMotionReady) return;
      section.dataset.taleMotionReady = 'true';
      const title = words(section.querySelector('h1, h2'));
      enter(section.querySelector('h1, h2'), () => title.forEach((word, i) => animate(word,
        [{transform: 'translateY(110%)', opacity: 0}, {transform: 'translateY(0)', opacity: 1}],
        700, section.id === 'opening' ? 100 + Math.min(i * 45, 180) : Math.min(i * 45, 180))));
      if (section.id === 'opening') {
        const photo = section.querySelector('.vte-media');
        enter(photo, () => whenImageReady(photo, () => animate(photo.querySelector('img'),
          [{transform: 'scale(1.035)'}, {transform: 'scale(1)'}], 1200)));
      } else if (section.matches('.vte--split')) {
        const photo = section.querySelector('.vte-media');
        enter(photo, () => whenImageReady(photo, () => imageArrival(photo, section.id === 'material' ? 'down' : 'side')));
      } else if (section.matches('.vte--rail, .vte--products')) {
        const track = section.querySelector('.vte-track, .vte-products');
        enter(track, () => [...track.children].filter(card => !card.inert).forEach((card, i) => {
          animate(card, [{transform: 'translateY(24px)', opacity: .5}, {transform: 'translateY(0)', opacity: 1}], 650, Math.min(i * 80, 160));
          const photo = card.querySelector('.vte-media');
          whenImageReady(photo, () => animate(photo.querySelector('img'), [{transform: 'scale(1.025)'}, {transform: 'scale(1)'}], 950));
        }));
      }
    });
    root.querySelectorAll('details').forEach(disclosure => {
      if (disclosure.dataset.taleFeedbackReady) return;
      disclosure.dataset.taleFeedbackReady = 'true';
      disclosure.addEventListener('toggle', () => {
        if (disclosure.open) animate(disclosure.querySelector('nav, .vte-body'),
          [{opacity: .25, transform: 'translateY(8px)'}, {opacity: 1, transform: 'translateY(0)'}], 250);
      });
    });
  };
  document.addEventListener('focusin', event => {
    active.forEach(animation => { if (animation.effect?.target?.contains(event.target)) animation.cancel(); });
  });
  const settle = () => { if (reduced.matches) active.forEach(animation => animation.cancel()); };
  reduced.addEventListener('change', settle);
  // Content is visible without JS, between animations, and when motion is reduced.
  document.addEventListener('visibilitychange', () => { if (document.hidden) active.forEach(animation => animation.finish()); });
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', event => {
    active.forEach(animation => { if (event.target.contains(animation.effect?.target)) animation.cancel(); });
    event.target.querySelectorAll('.vte, .vte-media, .vte-track, .vte-products').forEach(element => observer.unobserve(element));
  });
  window.vorugTaleMotion = {setup};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup()); else setup();
})();
